// LAN-984. The phrase rows of the Phrase ideas dialog
// (account/chat/phrase_ideas_dialog.js) and of the vocab deck screen
// (components/vocab-deck): the phrase, its translation (hidden until asked
// for), a translation toggle and an audio button, and optionally a badge and
// more buttons. Plain DOM, so the dialog stays out of React (it ships in the
// application pack); the deck's React island mounts the rows into its list.
// Card text is only ever set as textContent.

export function element (tag, className, text) {
  const el = document.createElement(tag)
  if (className) el.className = className
  if (text !== undefined) el.textContent = text
  return el
}

export function icon (name) {
  const el = element('i', `fa fa-${name}`)
  el.setAttribute('aria-hidden', 'true')
  return el
}

export function button (label, className, action) {
  const el = element('button', `btn ${className}`, label)
  el.type = 'button'
  el.addEventListener('click', action)
  return el
}

// 'idle' | 'loading' | 'playing'
export function setAudioState (audioButton, state) {
  audioButton.replaceChildren(icon({ idle: 'volume-up', loading: 'spinner fa-spin', playing: 'stop' }[state]))
  if (state === 'loading') audioButton.setAttribute('aria-busy', 'true')
  else audioButton.removeAttribute('aria-busy')
}

// items: [{ phrase, translation, badge? }]
// labels: { show, hide, play } (aria labels)
// onPlay(item, audioButton); onToggle(index, shown); extra(item) → an element
// appended at the end of the row, or nothing.
// @return [{ row, audio, setShown(shown) }] in the order of items
export function renderPhraseRows (list, items, { labels, onPlay, onToggle, extra } = {}) {
  list.replaceChildren()
  return items.map((item, index) => {
    const { phrase, translation, badge } = item
    const row = element('div', 'chat-speak-phrase-row')
    const text = element('div', 'chat-speak-phrase-text')
    const translated = element('div', 'chat-speak-phrase-translation', translation || '')
    translated.hidden = true
    text.append(element('strong', '', phrase))
    if (badge) text.append(element('span', 'chat-speak-phrase-badge', badge))
    text.append(translated)
    const setShown = (shown) => {
      translated.hidden = !shown
      translate.classList.toggle('is-shown', shown)
      translate.setAttribute('aria-pressed', String(shown))
      translate.setAttribute('aria-label', shown ? labels.hide : labels.show)
    }
    const translate = button('', 'chat-speak-phrase-icon', () => {
      setShown(translated.hidden)
      if (onToggle) onToggle(index, !translated.hidden)
    })
    translate.append(icon('language'))
    translate.disabled = !translation
    translate.setAttribute('aria-label', labels.show)
    translate.setAttribute('aria-pressed', 'false')
    const audio = button('', 'chat-speak-phrase-icon', () => onPlay(item, audio))
    audio.append(icon('volume-up'))
    audio.setAttribute('aria-label', labels.play)
    row.append(text, translate, audio)
    const more = extra && extra(item)
    if (more) row.append(more)
    list.append(row)
    return { row, audio, setShown }
  })
}

// One phrase plays at a time; its button shows loading, then stop while it
// plays. A second click on the playing button stops it.
// load(text) → Promise<{ audio, release? }>: audio is an HTMLAudioElement
//   (playing or not); release() frees it when it is no longer needed.
// cancel() stops a load in flight; onStart() runs when a phrase is asked
// for; onError() when it cannot play.
export class PhraseAudio {
  constructor ({ load, cancel, onStart, onError }) {
    Object.assign(this, { load, cancel, onStart, onError })
    this.version = 0
  }

  stop () {
    this.version += 1
    if (this.cancel) this.cancel()
    if (this.audio) this.audio.pause()
    this.audio = null
    if (this.release) this.release()
    this.release = null
    if (this.button) setAudioState(this.button, 'idle')
    this.button = null
  }

  async toggle (text, audioButton) {
    const same = this.button === audioButton
    this.stop()
    if (same) return
    const version = this.version
    this.button = audioButton
    setAudioState(audioButton, 'loading')
    if (this.onStart) this.onStart()
    try {
      const { audio, release, playbackRate } = await this.load(text)
      if (version !== this.version) { if (release) release(); return }
      this.audio = audio
      this.release = release
      // Eleven v4 audio plays at the user's speed tier (LAN-1177). Loading a
      // source resets playbackRate to defaultPlaybackRate, so set both.
      if (playbackRate) {
        audio.defaultPlaybackRate = playbackRate
        audio.playbackRate = playbackRate
      }
      audio.addEventListener('ended', () => { if (version === this.version) this.stop() }, { once: true })
      audio.addEventListener('error', () => {
        if (version !== this.version) return
        this.stop()
        if (this.onError) this.onError()
      }, { once: true })
      await audio.play()
      if (version === this.version) setAudioState(audioButton, 'playing')
    } catch (error) {
      console.error(error)
      if (version !== this.version) return
      this.stop()
      if (this.onError) this.onError()
    }
  }
}
